import { PlaneType } from '../../../game/Constants';
import { UPGRADES as GAME_UPGRADES } from '../../../game/GameData';

/**
 * Shared aircraft/upgrade definitions for the loadout UI surfaces: the
 * desktop sidebar (`aircraft-selector`, `upgrade-selector`) and the mobile
 * toolbar popover (`loadout-panel`). Pure display data — the game owns the
 * authoritative stats; these drive icons, labels and tooltip content only.
 */

/** Aircraft definitions. `cssClass` selects the background-image rule from
 *  the consuming component's stylesheet — no inline styles. */
export const AIRCRAFT = [
  {
    type: PlaneType.Predator,
    name: 'Predator',
    cssClass: 'predator',
    stats: { agility: 80, defense: 50, regen: 75, damage: 60 },
    ability: 'BOOST',
    abilityDesc: 'Temporarily increases speed by burning energy',
  },
  {
    type: PlaneType.Goliath,
    name: 'Goliath',
    cssClass: 'goliath',
    stats: { agility: 30, defense: 100, regen: 30, damage: 100 },
    ability: 'REPEL',
    abilityDesc: 'Repels enemy aircraft and missiles',
  },
  {
    type: PlaneType.Mohawk,
    name: 'Mohawk',
    cssClass: 'mohawk',
    stats: { agility: 100, defense: 40, regen: 100, damage: 40 },
    ability: 'STRAFE',
    abilityDesc: 'Enables sideways flight',
  },
  {
    type: PlaneType.Tornado,
    name: 'Tornado',
    cssClass: 'tornado',
    stats: { agility: 60, defense: 70, regen: 50, damage: 80 },
    ability: 'MULTIFIRE',
    abilityDesc: 'Launches 3 missiles at the same time',
  },
  {
    type: PlaneType.Prowler,
    name: 'Prowler',
    cssClass: 'prowler',
    stats: { agility: 50, defense: 70, regen: 40, damage: 60 },
    ability: 'STEALTH',
    abilityDesc: 'Becomes invisible to enemy aircraft',
  },
] as const;

/** Convenience alias for an entry in the AIRCRAFT array. */
export type Aircraft = (typeof AIRCRAFT)[number];

/** Upgrade button definitions. `index` is the 1–4 slot the game's
 *  `selectUpgrade` expects; `key` matches `uiState.upgradeLevels` and the
 *  game's `UPGRADES`, and also selects the per-upgrade background-image
 *  rule in the consuming components' stylesheets (no inline styles). */
export const UPGRADES = [
  { index: 1, key: 'speed', name: 'Speed' },
  { index: 2, key: 'defense', name: 'Defense' },
  { index: 3, key: 'energy', name: 'Energy Regen' },
  { index: 4, key: 'missile', name: 'Missile Speed' },
] as const;

/** Convenience alias for an entry in the UPGRADES array. */
export type Upgrade = (typeof UPGRADES)[number];

/** Stat multiplier at each upgrade level (array index = level); the tooltip
 *  renders it as "+X%". The game's own table, GameData.UPGRADES, which the
 *  upgrade notice reads too: this held a copy of it until 2026-10-21. */
export const UPGRADE_FACTORS: Readonly<Record<Upgrade['key'], readonly number[]>> = {
  speed: GAME_UPGRADES.speed.factor,
  defense: GAME_UPGRADES.defense.factor,
  energy: GAME_UPGRADES.energy.factor,
  missile: GAME_UPGRADES.missile.factor,
};

/** Highest level each upgrade can reach: the last index of the factor table, every line's being the same length. */
export const MAX_UPGRADE_LEVEL = GAME_UPGRADES.speed.factor.length - 1;
